/**
 * @file RefreshAutoLinksButton.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description "Refresh auto-links" in a contract screen's header (Cam, 28 Oct
 *              2026: "a button to refresh auto linking via a header slideout
 *              button and confirm dialogue with dropdown showing periods (so
 *              that user can only do for one period at a time). This should
 *              not remove manual links, but should convert them to auto if
 *              applicable").
 *
 *              The window offers this screen's periods only (periods are per
 *              category — never merged), shows what the refresh would do for
 *              the chosen one (a dry run: what is linked, moved, made
 *              automatic, kept and left, each with why) and applies exactly
 *              that. If orders or contracts change in between, the server
 *              refuses with the refresh as it now stands (409 REFRESH_CHANGED)
 *              and the window shows it again. The rules are the server's
 *              (utils/contractLinks.js).
 *
 *              1.1.0 (28 Oct 2026): also on Milk Powder, whose "periods" are
 *              months (utils/contractOrders.milkMonthPeriods); `permissions`
 *              names who may use it (default 'Edit contract pricing').
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { formatDateShort } from '../utils/dateFormatters';
import { formatQuantity } from '../utils/numberFormatters';
import useAccess from '../hooks/useAccess';
import { ACTION_WORDS } from '../utils/contractOrders';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import { useSlideOutButton } from './SlideOutButton';
import './ContractOrdersPanel.css';

const isoToday = () => new Date().toISOString().slice(0, 10);

/** The period covering today, else the latest one that has started, else the first. */
export function defaultPeriodId(periods) {
  const list = (periods || []).filter(p => p && p.id != null);
  if (!list.length) return '';
  const today = isoToday();
  const day = (v) => (v ? String(v).slice(0, 10) : '');
  const covering = list.find(p => day(p.start_date) && day(p.start_date) <= today && (!day(p.end_date) || day(p.end_date) >= today));
  if (covering) return String(covering.id);
  const started = list.filter(p => day(p.start_date) && day(p.start_date) <= today)
    .sort((a, b) => day(b.start_date).localeCompare(day(a.start_date)));
  return String((started[0] || list[0]).id);
}

function PlanTable({ rows, heading }) {
  if (!rows || rows.length === 0) return null;
  return (
    <>
      <h5>{heading} ({rows.length})</h5>
      <table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Delivery</th>
            <th>Customer</th>
            <th>Product</th>
            <th>What</th>
            <th>Contract</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={`${r.action}-${r.order_line_id}`}>
              <td className="nowrap"><strong>{r.order_number}</strong></td>
              <td className="nowrap">{formatDateShort(r.delivery_date)}</td>
              <td>{r.customer}</td>
              <td>{r.product}{r.quantity != null ? ` · ${formatQuantity(r.quantity)}` : ''}</td>
              <td className="refresh-auto-links-action">{ACTION_WORDS[r.action] || r.action}</td>
              <td>
                {r.action === 'move' ? `${r.from?.label} → ${r.to?.label}` : (r.to?.label || r.from?.label || '—')}
                {r.reason && <div className="refresh-auto-links-muted">{r.reason}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function RefreshAutoLinksButton({ periods = [], onDone, onAlert, label = 'Refresh Auto-links',
  permissions = ['contract_pricing.edit'], needsWords = "Needs 'Edit contract pricing'",
  intro = 'Links each order delivering in the period to the contract the order screen would price it from.' }) {
  const access = useAccess();
  const allowed = permissions.some(k => access.canWrite(k));
  const [open, setOpen] = useState(false);
  const [periodId, setPeriodId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [changedNote, setChangedNote] = useState(null);

  useSlideOutButton([allowed, applying]);

  const loadPreview = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const r = await fetch(`${API_URL}/api/pricing/contract-links/refresh/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_id: Number(id) })
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      setPreview(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && periodId) loadPreview(periodId);
  }, [open, periodId, loadPreview]);

  const start = () => {
    setChangedNote(null);
    setError(null);
    setPreview(null);
    setPeriodId(prev => prev || defaultPeriodId(periods));
    setOpen(true);
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/pricing/contract-links/refresh/apply`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_id: Number(periodId), token: preview.token })
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 409 && body.code === 'REFRESH_CHANGED' && body.preview) {
        setPreview(body.preview);
        setChangedNote(body.error);
        return;
      }
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      setOpen(false);
      onAlert?.({ isOpen: true, type: 'success', message: body.summary || 'Auto-links refreshed.' });
      if (onDone) await onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  const changes = preview?.changes || [];
  const nothing = preview && changes.length === 0;
  const confirmBlock = loading ? 'Working out what would change…'
    : !preview ? 'Choose a period'
      : nothing ? 'Nothing to change in this period'
        : null;

  return (
    <div>
      <button
        type="button"
        className={`btn-slideout${allowed ? '' : ' btn-disabled'}`}
        onClick={allowed ? start : undefined}
        disabled={!allowed}
        title={allowed
          ? 'Link orders to the contract that fits them, one period at a time — links added by hand are kept'
          : needsWords}
        data-write-action
      >
        <div className="icon-wrapper"><span className="material-symbols-rounded">sync_alt</span></div>
        <span className="btn-label">{label}</span>
      </button>

      <ConfirmDialog
        isOpen={open}
        type="info"
        title="Refresh auto-links"
        className="refresh-auto-links"
        confirmText={applying ? 'Refreshing…' : (changes.length ? `Make ${changes.length} change${changes.length === 1 ? '' : 's'}` : 'Refresh')}
        confirmIcon="sync_alt"
        confirmDisabled={!!confirmBlock || applying}
        confirmDisabledReason={confirmBlock}
        onConfirm={apply}
        onCancel={() => setOpen(false)}
        message={(
          <div className="refresh-auto-links-body">
            <p>
              {intro}{' '}
              Links added by hand are kept — and become automatic where the rule picks the same contract.
              Nothing on an invoiced order moves, and nothing is changed until you confirm.
            </p>
            <div className="refresh-auto-links-period">
              <label htmlFor="refresh-auto-links-period">Period</label>
              <Dropdown
                options={(periods || []).map(p => ({ value: String(p.id), label: p.name }))}
                value={periodId}
                onChange={(v) => { setChangedNote(null); setPeriodId(v); }}
                placeholder="Choose a period…"
              />
            </div>
            {loading && <p className="refresh-auto-links-muted">Working out what would change…</p>}
            {error && <p style={{ color: 'var(--color-error)' }}>{error}</p>}
            {changedNote && <p style={{ color: 'var(--color-error)' }}>{changedNote}</p>}
            {preview && (
              <>
                <p className="refresh-auto-links-summary">{preview.summary}</p>
                {(changes.length > 0 || (preview.notes || []).length > 0) && (
                  <div className="refresh-auto-links-list">
                    <PlanTable rows={changes} heading="Will change" />
                    <PlanTable rows={preview.notes} heading="Left as they are" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      />
    </div>
  );
}

export default RefreshAutoLinksButton;
